// src/modules/ida/components/FilePreviewChips.tsx
//
// Display file attachment chips for the chat input area and message history.
// Supports editable mode (with remove button) and read-only mode.

import React from "react";
import { Box, Chip, ChipDelete, Tooltip, Typography } from "@mui/joy";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import CodeIcon from "@mui/icons-material/Code";
import ArticleIcon from "@mui/icons-material/Article";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";

/** File info for display */
export interface FilePreviewInfo {
  filename: string;
  fileSize: number;
  mimeType?: string;
  thumbnailUrl?: string;
}

interface FilePreviewChipsProps {
  files: FilePreviewInfo[];
  onRemove?: (index: number) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
}

/** Allowed file extensions for chat uploads */
export const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".doc",
  ".xlsx",
  ".xls",
  ".pptx",
  ".ppt",
  ".txt",
  ".md",
  ".csv",
  ".json",
  ".html",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".bmp",
  ".svg",
  ".tiff",
  ".tif",
];

/** Max file size: 10MB */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Check if a file has an allowed extension */
export const isAllowedFile = (filename: string): boolean => {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf("."));
  return ALLOWED_EXTENSIONS.includes(ext);
};

/** Check if a file is within size limit */
export const isAllowedFileSize = (file: File): boolean => {
  return file.size > 0 && file.size <= MAX_FILE_SIZE;
};

/** Format file size for display */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Get icon for file based on extension */
const getFileIcon = (filename: string): React.ReactNode => {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf("."));
  switch (ext) {
    case ".pdf":
      return <PictureAsPdfIcon fontSize="small" />;
    case ".doc":
    case ".docx":
      return <DescriptionIcon fontSize="small" />;
    case ".xls":
    case ".xlsx":
    case ".csv":
      return <TableChartIcon fontSize="small" />;
    case ".ppt":
    case ".pptx":
      return <SlideshowIcon fontSize="small" />;
    case ".png":
    case ".jpg":
    case ".jpeg":
    case ".gif":
    case ".webp":
    case ".bmp":
    case ".svg":
    case ".tiff":
    case ".tif":
      return <ImageIcon fontSize="small" />;
    case ".json":
    case ".html":
      return <CodeIcon fontSize="small" />;
    case ".txt":
    case ".md":
      return <ArticleIcon fontSize="small" />;
    default:
      return <InsertDriveFileOutlinedIcon fontSize="small" />;
  }
};

/** Truncate filename if too long */
const truncateFilename = (filename: string, maxLength: number = 25): string => {
  if (filename.length <= maxLength) return filename;
  const ext = filename.slice(filename.lastIndexOf("."));
  const name = filename.slice(0, filename.lastIndexOf("."));
  const truncatedName = name.slice(0, maxLength - ext.length - 3) + "...";
  return truncatedName + ext;
};

/**
 * FilePreviewChips component.
 * Displays file attachments as chips with icons and optional remove button.
 */
export const FilePreviewChips: React.FC<FilePreviewChipsProps> = ({
  files,
  onRemove,
  disabled = false,
  size = "md",
}) => {
  if (files.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        py: 0.5,
      }}
    >
      {files.map((file, index) => (
        <Tooltip
          key={index}
          title={
            <Box>
              <Typography level="body-xs">{file.filename}</Typography>
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                {formatFileSize(file.fileSize)}
              </Typography>
            </Box>
          }
          placement="top"
        >
          <Chip
            size={size}
            variant="soft"
            color="neutral"
            disabled={disabled}
            startDecorator={
              file.thumbnailUrl ? (
                <Box
                  component="img"
                  src={file.thumbnailUrl}
                  alt=""
                  sx={{
                    width: size === "sm" ? 18 : 22,
                    height: size === "sm" ? 18 : 22,
                    objectFit: "cover",
                    borderRadius: 2,
                    display: "block",
                  }}
                />
              ) : (
                getFileIcon(file.filename)
              )
            }
            endDecorator={
              onRemove && !disabled ? (
                <ChipDelete
                  aria-label={`Remove ${file.filename}`}
                  onDelete={(e) => {
                    e.stopPropagation();
                    onRemove(index);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </ChipDelete>
              ) : undefined
            }
            sx={{
              "--Chip-minHeight": size === "sm" ? "24px" : "28px",
              maxWidth: "180px",
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {truncateFilename(file.filename)}
            </Typography>
          </Chip>
        </Tooltip>
      ))}
    </Box>
  );
};

export default FilePreviewChips;
